import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Stats, ValidationStats, HealthStatus } from '@/types'
import { fetchStats, fetchValidationStats, fetchReady } from '@/api/client'

export const DEBOUNCE_MS = 300
export const AUTO_REFRESH_INTERVAL_MS = 30000

export const useStatsStore = defineStore('stats', () => {
  const stats = ref<Stats | null>(null)
  const validationStats = ref<ValidationStats | null>(null)
  const health = ref<HealthStatus | null>(null)
  const loading = ref(false)

  // Date range state
  const dateFrom = ref<string | null>(null)
  const dateTo = ref<string | null>(null)

  // Auto-refresh
  let refreshInterval: ReturnType<typeof setInterval> | null = null
  let loadAllInFlight: Promise<void> | null = null

  // Debounce
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const isHealthy = computed(() => health.value?.status === 'healthy')
  const isDegraded = computed(() => health.value?.status === 'degraded')

  function loadAllDebounced() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(loadAll, DEBOUNCE_MS)
  }

  function setDateRange(from: string | null, to: string | null) {
    dateFrom.value = from
    dateTo.value = to
    loadAllDebounced()
  }

  async function loadStats() {
    try {
      stats.value = await fetchStats(dateFrom.value ?? undefined, dateTo.value ?? undefined)
    } catch (e) {
      // Silently fail, show stale data
    }
  }

  async function loadValidationStats() {
    try {
      validationStats.value = await fetchValidationStats(dateFrom.value ?? undefined, dateTo.value ?? undefined)
    } catch (e) {
      // Silently fail
    }
  }

  async function loadHealth() {
    try {
      health.value = await fetchReady()
    } catch (e) {
      health.value = {
        status: 'unhealthy',
        services: {},
        timestamp: new Date().toISOString(),
      }
    }
  }

  function loadAll(): Promise<void> {
    // A slow aggregate must not be duplicated every 30 seconds. Stacked
    // requests used to consume the API connection pool during DB maintenance.
    if (loadAllInFlight) return loadAllInFlight
    loading.value = true
    loadAllInFlight = Promise.all([loadStats(), loadValidationStats(), loadHealth()])
      .then(() => undefined)
      .finally(() => {
        loading.value = false
        loadAllInFlight = null
      })
    return loadAllInFlight
  }

  function startAutoRefresh(intervalMs = AUTO_REFRESH_INTERVAL_MS) {
    stopAutoRefresh()
    const tick = () => {
      // Pause polling when tab is hidden to reduce server load
      if (document.hidden) return
      loadAll()
    }
    refreshInterval = setInterval(tick, intervalMs)
  }

  function stopAutoRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  return {
    stats,
    validationStats,
    health,
    loading,
    dateFrom,
    dateTo,
    isHealthy,
    isDegraded,
    setDateRange,
    loadStats,
    loadValidationStats,
    loadHealth,
    loadAll,
    startAutoRefresh,
    stopAutoRefresh,
  }
})
